import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { getHeaders } from '../../../lib/requests';

function MenuMultiSelect({ label, onSelect, selectedMenus, menus }) {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!menus || menus.length === 0) {
      const fetchMenus = async () => {
        try {
          const apiToken = localStorage.getItem('accessToken');
          const apiUrl = process.env.REACT_APP_API_URL;
          const response = await axios.get(`${apiUrl}/menus`, {
            headers: getHeaders(apiToken),
          });
          setItems([{ menu_id: null, name: 'No Menu Selected' }, ...response.data.data]);
        } catch (error) {
          console.error('Error fetching menus', error);
        }
      };
      fetchMenus();
    } else {
      setItems([{ menu_id: null, name: 'No Menu Selected' }, ...menus]);
    }
  }, [menus]);

  useEffect(() => {
    if (selectedMenus && selectedMenus.length > 0) {
      const filtered = items.filter(item =>
        selectedMenus.some(sel => sel.menu_id === item.menu_id || sel.name === item.name)
      );
      setSelectedItems(filtered);
    } else {
      setSelectedItems([]);
    }
  }, [selectedMenus, items]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => setShowDropdown(!showDropdown);

  const handleCheckboxChange = (item) => {
    let newSelected;
    if (selectedItems.find(sel => sel.menu_id === item.menu_id)) {
      newSelected = selectedItems.filter(sel => sel.menu_id !== item.menu_id);
    } else {
      newSelected = [...selectedItems, item];
    }
    setSelectedItems(newSelected);
    onSelect(newSelected);
  };

  const filteredItems = useMemo(() => {
    if (!searchText) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [items, searchText]);

  return (
    <div className="relative inline-block" style={{ minWidth: '14rem' }}>
      <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
        {label}
      </label>
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedItems.length > 0 ? (
          selectedItems.map(item => (
            <span
              key={item.menu_id}
              className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded truncate"
            >
              {item.menu_type ? `${item.name} (${item.menu_type})` : item.name}
            </span>
          ))
        ) : (
          <span className="text-gray-500 text-xs">No menu selected</span>
        )}
      </div>
      <button
        type="button"
        onClick={toggleDropdown}
        className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex items-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
      >
        {showDropdown ? 'Close' : 'Select Menus'}
        <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 20 20">
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 8l5 5 5-5"
          />
        </svg>
      </button>
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-10 mt-2 bg-white divide-y divide-gray-100 rounded shadow dark:bg-gray-700"
          style={{ width: '100%', minWidth: '14rem', maxHeight: '20rem', overflowY: 'auto' }}
        >
          <div className="p-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  fill="none"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 19l-4-4m0-7a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                placeholder="Search menus"
              />
            </div>
          </div>
          <ul className="py-1 text-sm text-gray-700 dark:text-gray-200">
            {filteredItems.map(item => (
              <li key={item.menu_id}>
                <div className="flex items-center pl-4 pr-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600">
                  <input
                    id={`checkbox-${item.menu_id}`}
                    type="checkbox"
                    checked={!!selectedItems.find(sel => sel.menu_id === item.menu_id)}
                    onChange={() => handleCheckboxChange(item)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-700 dark:focus:ring-offset-gray-700 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
                  />
                  <label
                    htmlFor={`checkbox-${item.menu_id}`}
                    className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300 truncate"
                  >
                    {item.menu_type ? `${item.name} (${item.menu_type})` : item.name}
                  </label>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

MenuMultiSelect.propTypes = {
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedMenus: PropTypes.array,
  menus: PropTypes.array,
};

MenuMultiSelect.defaultProps = {
  menus: [],
  selectedMenus: [],
};

export default MenuMultiSelect;
